class ArtsPanelController {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;
  }

  getArtCategories() {
    const categoriesByKey = new Map();
    this.getAvailableArts().forEach(art => {
      if (art.category?.key) {
        categoriesByKey.set(art.category.key, art.category.name);
      }
    });
    const list = [...categoriesByKey.entries()].map(([key, name]) => ({ key, name }));
    // Brasil sempre por último
    return [
      ...list.filter(category => category.key !== 'brasil'),
      ...list.filter(category => category.key === 'brasil'),
    ];
  }

  // Estampas que cobrem a garrafa toda (arquivos "Full-360")
  isArt360(art) {
    return /Full-360/i.test(art?.url || '');
  }

  getArtCategoriesList() {
    const has360 = this.getAvailableArts().some(art => this.isArt360(art));
    return [
      { key: 'todas', name: 'Todas' },
      ...(has360 ? [{ key: '360', name: '360º' }] : []),
      ...this.getArtCategories(),
    ];
  }

  getActiveCategoryName() {
    const { activeCategoryKey } = this.orchestrator.selectionState;
    const category = this.getArtCategoriesList().find(
      item => item.key === activeCategoryKey,
    );
    return category?.name || 'Todas';
  }

  getFilteredArts() {
    const { selectionState } = this.orchestrator;
    const availableArts = this.getAvailableArts();
    if (selectionState.activeCategoryKey === 'todas') return availableArts;
    if (selectionState.activeCategoryKey === '360') {
      return availableArts.filter(art => this.isArt360(art));
    }
    return availableArts.filter(
      art => art.category?.key === selectionState.activeCategoryKey &&
        !(selectionState.activeCategoryKey === 'padrao' && this.isArt360(art)),
    );
  }

  getAvailableArts() {
    const { ARTS, selectionState } = this.orchestrator;
    if (selectionState.style !== 'textprint') return ARTS;
    return ARTS.filter(art => art.textCompatible !== 'incompatible');
  }

  isArtAllowedForColor(
    art,
    colorKey = this.orchestrator.selectionState.selectedColorKey,
  ) {
    const blocked = art?.colorsNotAllowed;
    if (!Array.isArray(blocked) || blocked.length === 0) return true;
    return !blocked.includes(colorKey);
  }

  syncUiAfterArtChange() {
    const {
      textCustomizationController,
      stepNavigationController,
      finishCheckoutController,
      bottleSceneController,
    } = this.orchestrator;
    const art = this.orchestrator.selectionState.getSelectedArt();

    textCustomizationController.syncTextLayoutOptions();
    stepNavigationController.syncStepNavAvailability();
    stepNavigationController.syncNextStepButton();

    if (stepNavigationController.getActiveStepKey() === 'review') {
      finishCheckoutController.syncFinishStep();
    }
    if (
      !stepNavigationController.canNavigateToFinish() &&
      stepNavigationController.getActiveStepKey() === 'review'
    ) {
      stepNavigationController.setActiveStep('content');
    }

    bottleSceneController.applySelection();
    this.syncRemoveArtButton();
  }

  clearSelectedArtIfDisallowed() {
    const { selectionState } = this.orchestrator;
    const art = selectionState.getSelectedArt();
    if (!art || this.isArtAllowedForColor(art)) return false;

    selectionState.selectedArtKey = null;
    selectionState.art = null;
    selectionState.accepted = false;
    this.syncUiAfterArtChange();
    return true;
  }

  clearSelectedArt() {
    const { selectionState } = this.orchestrator;
    if (!selectionState.selectedArtKey) return false;

    selectionState.selectedArtKey = null;
    selectionState.art = null;
    selectionState.accepted = false;
    this.renderArtsGrid();
    this.syncUiAfterArtChange();
    return true;
  }

  syncRemoveArtButton() {
    const { stepContent } = this.orchestrator.dom;
    const button = stepContent?.querySelector('.arts-remove-selection');
    if (!button) return;

    button.hidden = !this.orchestrator.selectionState.selectedArtKey;
  }

  closeArtsCategoryMenu() {
    const { stepContent } = this.orchestrator.dom;
    const menu = stepContent?.querySelector('.arts-category-menu');
    const trigger = stepContent?.querySelector('.arts-category-trigger');
    if (menu) menu.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  syncArtsGridFade() {
    const { selectionState } = this.orchestrator;
    const { stepContent } = this.orchestrator.dom;

    if (
      !selectionState.artsGridWrapElement ||
      !selectionState.artsGridElement
    ) {
      selectionState.artsGridWrapElement =
        stepContent?.querySelector('.arts-grid-wrap');
      selectionState.artsGridElement = stepContent?.querySelector('.arts-grid');
    }
    if (
      !selectionState.artsGridWrapElement ||
      !selectionState.artsGridElement
    ) {
      return;
    }

    const grid = selectionState.artsGridElement;
    const remaining = grid.scrollWidth - grid.scrollLeft - grid.clientWidth;
    const hasMore = remaining > 8;
    if (selectionState.artsHasMoreCached === hasMore) return;
    selectionState.artsHasMoreCached = hasMore;
    selectionState.artsGridWrapElement.classList.toggle('has-more', hasMore);
  }

  scheduleArtsGridFade() {
    const { selectionState } = this.orchestrator;
    if (selectionState.artsFadeAnimationFrame) return;
    selectionState.artsFadeAnimationFrame = requestAnimationFrame(() => {
      selectionState.artsFadeAnimationFrame = 0;
      this.syncArtsGridFade();
    });
  }

  handleArtCardSelection(art, grid, button) {
    const { selectionState } = this.orchestrator;

    if (!this.isArtAllowedForColor(art)) return;

    if (selectionState.selectedArtKey === art.key) {
      selectionState.selectedArtKey = null;
      selectionState.art = null;
      selectionState.accepted = false;
      button.classList.remove('active');
      this.syncUiAfterArtChange();
      return;
    }

    selectionState.selectedArtKey = art.key;
    selectionState.art = art.key;
    selectionState.accepted = false;
    grid
      .querySelectorAll('.art-card')
      .forEach(card => card.classList.remove('active'));
    button.classList.add('active');
    this.syncUiAfterArtChange();
  }

  renderArtsGrid() {
    const { selectionState } = this.orchestrator;
    const { stepContent } = this.orchestrator.dom;
    const availableCategoryKeys = new Set(
      this.getArtCategoriesList().map(category => category.key),
    );
    if (!availableCategoryKeys.has(selectionState.activeCategoryKey)) {
      selectionState.activeCategoryKey = 'todas';
      selectionState.category = 'todas';
    }
    stepContent
      .querySelectorAll('.arts-category-option')
      .forEach(option => {
        const isAvailable = availableCategoryKeys.has(option.dataset.category);
        option.hidden = !isAvailable;
        const isActive =
          option.dataset.category === selectionState.activeCategoryKey;
        option.classList.toggle('active', isActive);
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    const grid =
      selectionState.artsGridElement || stepContent.querySelector('.arts-grid');
    if (!grid) return;
    selectionState.artsGridElement = grid;

    const arts = this.getFilteredArts();
    const fragment = document.createDocumentFragment();
    const colorKey = selectionState.selectedColorKey;

    arts.forEach(art => {
      const isBlocked = !this.isArtAllowedForColor(art, colorKey);
      const button = document.createElement('button');
      button.type = 'button';
      const isFull = String(art.url || '').includes('Full-360');
      button.className = `art-card art-card--${isFull ? 'full' : 'icon'}`;
      button.dataset.art = art.key;
      button.title = isBlocked
        ? `${art.name} (indisponível nesta cor)`
        : art.name;
      button.setAttribute('aria-label', button.title);

      if (isBlocked) {
        button.classList.add('disabled');
        button.disabled = true;
      }

      if (!isBlocked && selectionState.selectedArtKey === art.key) {
        button.classList.add('active');
      }

      button.innerHTML = `
      <span class="art-card__media">
        <img
          src="${art.previewUrl || art.url}"
          alt="${art.name}"
          loading="lazy"
          decoding="async"
          crossorigin="anonymous"
        />
      </span>
    `;

      button.addEventListener('click', () => {
        this.handleArtCardSelection(art, grid, button);
      });

      fragment.appendChild(button);
    });

    grid.replaceChildren(fragment);
    selectionState.artsHasMoreCached = null;
    this.scheduleArtsGridFade();
  }

  // Faixa de categorias com mouse: a roda vertical rola para os lados e dá
  // para clicar e arrastar. No toque e no trackpad o navegador já resolve.
  enableMouseHorizontalScroll(element) {
    element.addEventListener(
      'wheel',
      event => {
        if (event.ctrlKey) return;
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        const maxScroll = element.scrollWidth - element.clientWidth;
        if (maxScroll <= 0) return;
        const next = element.scrollLeft + event.deltaY;
        if ((event.deltaY < 0 && element.scrollLeft <= 0) || (event.deltaY > 0 && element.scrollLeft >= maxScroll)) return;
        event.preventDefault();
        element.scrollLeft = Math.max(0, Math.min(maxScroll, next));
      },
      { passive: false },
    );

    let dragStartX = 0;
    let dragStartScroll = 0;
    let pointerId = null;
    let dragged = false;

    element.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScroll = element.scrollLeft;
      dragged = false;
    });

    element.addEventListener('pointermove', event => {
      if (event.pointerId !== pointerId) return;
      const deltaX = event.clientX - dragStartX;
      if (!dragged && Math.abs(deltaX) < 5) return;
      if (!dragged) {
        dragged = true;
        element.setPointerCapture(pointerId);
        element.classList.add('is-dragging');
      }
      element.scrollLeft = dragStartScroll - deltaX;
    });

    const endDrag = event => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      element.classList.remove('is-dragging');
    };
    element.addEventListener('pointerup', endDrag);
    element.addEventListener('pointercancel', endDrag);

    // Depois de arrastar, o clique que vem junto não troca de categoria.
    element.addEventListener(
      'click',
      event => {
        if (!dragged) return;
        dragged = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true,
    );
  }

  buildArtsPanel() {
    const { selectionState } = this.orchestrator;
    const { stepContent } = this.orchestrator.dom;
    if (!stepContent || stepContent.dataset.ready === 'true') return;
    stepContent.dataset.ready = 'true';

    const filter = document.createElement('div');
    filter.className = 'arts-category-filter';
    filter.setAttribute('role', 'listbox');
    filter.setAttribute('aria-label', 'Categorias de estampas');

    const categories = this.getArtCategoriesList();
    categories.forEach(category => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'arts-category-option';
      option.dataset.category = category.key;
      option.setAttribute('role', 'option');
      option.setAttribute(
        'aria-selected',
        category.key === selectionState.activeCategoryKey ? 'true' : 'false',
      );
      if (category.key === selectionState.activeCategoryKey) {
        option.classList.add('active');
      }

      option.textContent = category.name;

      option.addEventListener('click', () => {
        selectionState.activeCategoryKey = category.key;
        selectionState.category = category.key;

        filter.querySelectorAll('.arts-category-option').forEach(item => {
          const isActive =
            item.dataset.category === selectionState.activeCategoryKey;
          item.classList.toggle('active', isActive);
          item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.renderArtsGrid();
      });

      filter.appendChild(option);
    });

    this.enableMouseHorizontalScroll(filter);

    const gridWrap = document.createElement('div');
    gridWrap.className = 'arts-grid-wrap';
    selectionState.artsGridWrapElement = gridWrap;

    const grid = document.createElement('div');
    grid.className = 'arts-grid';
    selectionState.artsGridElement = grid;
    grid.addEventListener('scroll', () => this.scheduleArtsGridFade(), {
      passive: true,
    });

    gridWrap.appendChild(grid);
    stepContent.appendChild(filter);
    stepContent.appendChild(gridWrap);
    this.renderArtsGrid();
    window.addEventListener('resize', () => this.scheduleArtsGridFade());
  }
}

window.ArtsPanelController = ArtsPanelController;
